import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { trackEvent } from "@/lib/analytics";
import {
  ArrowRight,
  ChevronRight,
  Check,
  Dumbbell,
  Sparkles,
  HeartPulse,
  Target,
  Briefcase,
  Phone,
  MessageCircle,
  Mail,
  MapPin,
} from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const PHONE = "07386710866";
const PHONE_INTL = "447386710866";
const EMAIL = "mateobrysonpt@gmail.com";
const WHATSAPP_TEXT = encodeURIComponent(
  "Hi Mateo, I'd like to enquire about private personal training in London."
);

const HERO_IMG =
  "https://customer-assets.emergentagent.com/job_e50a4bf5-1bdf-41a0-aee8-165d65d803a2/artifacts/it2ev96t_IMG_4773.jpeg";

const CANONICAL_URL = "https://mateobrysonpt.com/private-personal-trainer-london";

// ------------- CROSS-PAGE NAV -------------
// A variant of the homepage nav whose in-page links resolve back to '/'.
const Nav = () => {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = [
    { href: "/#services", label: "Services" },
    { href: "/#how", label: "Method" },
    { href: "/#about", label: "About" },
    { href: "/#pricing", label: "Pricing" },
    { href: "/#faq", label: "FAQ" },
  ];

  return (
    <nav
      data-testid="london-nav"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-black/75 backdrop-blur-xl border-b border-white/5"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 flex items-center justify-between h-20">
        <Link
          to="/"
          data-testid="london-nav-logo"
          className="flex items-center gap-3 group"
        >
          <span className="h-8 w-8 border border-[#D4AF37]/60 flex items-center justify-center text-[#D4AF37] font-serif text-sm tracking-widest">
            M
          </span>
          <span className="font-serif text-base tracking-widest text-[#f4f4f0] group-hover:text-[#D4AF37] transition-colors">
            MATEO&nbsp;BRYSON
          </span>
        </Link>

        <div className="hidden md:flex items-center gap-10">
          {links.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`london-nav-${l.label.toLowerCase()}`}
              className="text-[13px] tracking-[0.18em] uppercase text-[#a1a1aa] hover:text-[#D4AF37] transition-colors"
            >
              {l.label}
            </a>
          ))}
        </div>

        <a
          href="/#contact"
          data-testid="london-nav-cta"
          className="group hidden sm:inline-flex items-center gap-2 border border-[#D4AF37]/70 text-[#D4AF37] hover:bg-[#D4AF37] hover:text-black transition-all duration-300 px-5 py-2.5 text-[12px] tracking-[0.22em] uppercase"
        >
          Enquire
          <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-1" />
        </a>
      </div>
    </nav>
  );
};

// ------------- HEAD MANAGEMENT (SEO) -------------
// Each route owns its own SEO effect that unconditionally (re)asserts
// title / description / canonical / og:* on mount. We do NOT try to
// restore previous values on unmount — that pattern is fragile under
// React 18 StrictMode (double-invoked effects capture already-mutated
// state as "previous") and leaks route-specific SEO across navigations.
const useSeoHead = () => {
  useEffect(() => {
    const setMeta = (selector, attr, value) => {
      let el = document.head.querySelector(selector);
      if (!el) {
        el = document.createElement(selector.startsWith("link") ? "link" : "meta");
        if (selector.startsWith("link")) {
          el.setAttribute("rel", "canonical");
        } else if (selector.includes("property=")) {
          el.setAttribute("property", selector.match(/property="([^"]+)"/)[1]);
        } else if (selector.includes("name=")) {
          el.setAttribute("name", selector.match(/name="([^"]+)"/)[1]);
        }
        document.head.appendChild(el);
      }
      el.setAttribute(attr, value);
    };

    document.title = "Private Personal Trainer London | Mateo Bryson";
    setMeta(
      'meta[name="description"]',
      "content",
      "Bespoke private personal training in Chelsea, Knightsbridge and West London. One-to-one coaching with Mateo Bryson, at home or in a private gym."
    );
    setMeta('link[rel="canonical"]', "href", CANONICAL_URL);
    setMeta('meta[name="robots"]', "content", "index,follow,max-image-preview:large");
    setMeta('meta[property="og:title"]', "content", "Private Personal Trainer London | Mateo Bryson");
    setMeta(
      'meta[property="og:description"]',
      "content",
      "Bespoke one-to-one personal training across London — delivered at home, in private gyms or selected locations."
    );
    setMeta('meta[property="og:url"]', "content", CANONICAL_URL);

    // Inject page-scoped Service JSON-LD, remove on unmount.
    const SERVICE_LD_ID = "ld-service-london";
    let ldScript = document.getElementById(SERVICE_LD_ID);
    if (!ldScript) {
      ldScript = document.createElement("script");
      ldScript.type = "application/ld+json";
      ldScript.id = SERVICE_LD_ID;
      document.head.appendChild(ldScript);
    }
    ldScript.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Service",
      name: "Private Personal Training in London",
      serviceType: "Private Personal Training",
      url: CANONICAL_URL,
      description:
        "One-to-one private personal training with Mateo Bryson, delivered at the client's home, at a suitable private gym, or another private location across London.",
      provider: {
        "@type": "Person",
        name: "Mateo Bryson",
        jobTitle: "Private Mobile Personal Trainer",
        telephone: "+44 7386 710866",
        email: "mateobrysonpt@gmail.com",
        url: "https://mateobrysonpt.com/",
      },
      areaServed: [
        { "@type": "Place", name: "Chelsea, London (SW3)" },
        { "@type": "Place", name: "Kensington, London (W8)" },
        { "@type": "Place", name: "Knightsbridge & Belgravia, London (SW1)" },
        { "@type": "Place", name: "Fulham, London (SW6, SW10)" },
        { "@type": "Place", name: "Mayfair & Marylebone, London (W1)" },
        { "@type": "Place", name: "Notting Hill & Holland Park, London (W11)" },
      ],
      hasOfferCatalog: {
        "@type": "OfferCatalog",
        name: "Coaching areas",
        itemListElement: [
          { "@type": "Offer", itemOffered: { "@type": "Service", name: "Bespoke Private Personal Training" } },
          { "@type": "Offer", itemOffered: { "@type": "Service", name: "Strength & Conditioning" } },
          { "@type": "Offer", itemOffered: { "@type": "Service", name: "Body Composition & Fitness" } },
          { "@type": "Offer", itemOffered: { "@type": "Service", name: "Mobility & Movement Quality" } },
          { "@type": "Offer", itemOffered: { "@type": "Service", name: "Training for Busy Professionals" } },
        ],
      },
    });

    return () => {
      // Remove page-scoped JSON-LD so it doesn't leak into other routes.
      // We intentionally do NOT restore previous <head> values — the next
      // route's own SEO effect is the sole source of truth.
      document.getElementById(SERVICE_LD_ID)?.remove();
    };
  }, []);
};

// ------------- HERO -------------
const Hero = () => (
  <section
    data-testid="london-hero"
    className="relative min-h-[90svh] w-full overflow-hidden"
  >
    <div className="absolute inset-0">
      <img
        src={HERO_IMG}
        alt="Mateo Bryson — private personal trainer, London"
        className="h-full w-full object-cover object-[60%_center] sm:object-[70%_center]"
        loading="eager"
      />
      <div className="absolute inset-0 bg-gradient-to-r from-black via-black/75 to-transparent" />
      <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-black/40" />
    </div>

    <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 pt-40 pb-24 min-h-[90svh] flex flex-col justify-center">
      <div className="max-w-3xl">
        <p className="overline reveal" data-testid="london-overline">
          Private personal trainer · London
        </p>
        <h1
          data-testid="london-h1"
          className="headline text-white text-[40px] sm:text-6xl lg:text-[72px] mt-6 reveal reveal-delay-1"
        >
          Private Personal Trainer{" "}
          <span className="italic text-[#D4AF37]">in London</span>
        </h1>
        <p
          data-testid="london-subcopy"
          className="mt-8 text-[#d4d4d0] text-base sm:text-lg max-w-2xl leading-relaxed reveal reveal-delay-2"
        >
          Highly personalised one-to-one coaching for people who want their
          training to actually fit their life. Sessions delivered at home,
          in private gyms, or wherever suits — quiet, considered, and built
          entirely around you.
        </p>

        <div className="mt-10 flex flex-col sm:flex-row gap-4 reveal reveal-delay-3">
          <a
            href="/#contact"
            data-testid="london-hero-cta-primary"
            onClick={() =>
              trackEvent("cta_book_consultation_click", {
                location: "london_hero",
              })
            }
            className="group inline-flex items-center justify-center gap-3 bg-[#D4AF37] text-black px-8 py-4 text-[12px] tracking-[0.22em] uppercase font-medium hover:bg-[#f0e6d2] transition-colors"
          >
            Request a Consultation
            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
          </a>
          <a
            href={`tel:${PHONE}`}
            data-testid="london-hero-cta-call"
            onClick={() =>
              trackEvent("call_button_click", { location: "london_hero" })
            }
            className="inline-flex items-center justify-center gap-3 border border-white/30 text-white px-8 py-4 text-[12px] tracking-[0.22em] uppercase hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors"
          >
            <Phone className="h-4 w-4" />
            Call 07386 710 866
          </a>
        </div>
      </div>
    </div>

    <div className="absolute bottom-8 right-8 z-10 hidden lg:flex items-center gap-2 text-[10px] tracking-[0.3em] uppercase text-white/50">
      <MapPin className="h-3 w-3" />
      London · Home · Private Studios
    </div>
  </section>
);

// ------------- INTRO -------------
const Intro = () => (
  <section
    data-testid="london-intro"
    className="relative py-24 sm:py-32 bg-[#0a0a0a]"
  >
    <div className="max-w-4xl mx-auto px-6 lg:px-10">
      <p className="overline">A more personal approach</p>
      <p className="mt-8 text-[#d4d4d0] text-lg sm:text-xl leading-[1.7] font-light">
        Mateo Bryson is a private personal trainer working across London with
        clients who value discretion, precision and the kind of coaching that
        is properly designed for them. Not a franchise, not a template — an
        experienced one-to-one coach who has spent the last decade quietly
        producing results for people who take their training seriously.
      </p>
      <div className="divider-gold my-10" />
      <p className="text-[#a1a1aa] text-base sm:text-lg leading-relaxed">
        Whether you train best in the calm of your own home, prefer a
        well-equipped private gym, or want a combination of both, sessions are
        shaped around your goals, your schedule and the environment that gets
        the most out of you.
      </p>
    </div>
  </section>
);

// ------------- CORE SERVICE PILLARS -------------
const Pillars = () => {
  const pillars = [
    {
      icon: Sparkles,
      title: "Bespoke Private Personal Training",
      text: "Every session is written specifically for you. Your body, your history, your ambitions — and the honest daily reality of your calendar. Programming is refined, not padded.",
    },
    {
      icon: Dumbbell,
      title: "Strength & Conditioning",
      text: "Intelligent resistance work that builds usable strength, resilient joints and quiet, functional power. Suitable for beginners returning to training and for experienced athletes alike.",
    },
    {
      icon: Target,
      title: "Body Composition & Fitness",
      text: "Sustainable changes to how you look, move and feel — combining well-structured training with clear, evidence-based nutrition guidance. No fads, no extremes.",
    },
    {
      icon: HeartPulse,
      title: "Mobility & Movement Quality",
      text: "Considered work on how you move. Restoring range, correcting long-standing compensations, and building the movement literacy that makes every other goal easier to reach.",
    },
    {
      icon: Briefcase,
      title: "Training for Busy Professionals",
      text: "Coaching designed around demanding diaries. Efficient, focused sessions that respect your time — and produce meaningful, measurable progress week on week.",
    },
  ];

  return (
    <section
      id="services"
      data-testid="london-pillars"
      className="relative py-24 sm:py-32 bg-[#111111] border-y border-white/5"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid lg:grid-cols-12 gap-12 mb-16">
          <div className="lg:col-span-6">
            <p className="overline">What the coaching covers</p>
            <h2 className="headline text-[#f4f4f0] text-3xl sm:text-4xl lg:text-5xl mt-5">
              Coaching, considered.
            </h2>
          </div>
          <div className="lg:col-span-5 lg:col-start-8 flex items-end">
            <p className="text-[#a1a1aa] text-base leading-relaxed">
              The work spans five interconnected areas. Most clients touch all
              of them at different points — the emphasis shifts as your body
              and priorities do.
            </p>
          </div>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-white/5">
          {pillars.map((p, i) => {
            const Icon = p.icon;
            return (
              <article
                key={p.title}
                data-testid={`london-pillar-${i}`}
                className="bg-[#0a0a0a] p-10 hover:bg-[#141414] transition-colors duration-500"
              >
                <Icon className="h-6 w-6 text-[#D4AF37]" strokeWidth={1.25} />
                <h3 className="font-serif text-[22px] sm:text-[24px] text-[#f4f4f0] mt-6 leading-snug">
                  {p.title}
                </h3>
                <div className="divider-gold my-5" />
                <p className="text-[#a1a1aa] leading-relaxed text-[15px]">
                  {p.text}
                </p>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
};

// ------------- HOW IT WORKS -------------
const HowItWorks = () => {
  const steps = [
    {
      n: "I",
      title: "Initial conversation",
      text: "A private call to understand what you want from training, your history, and how it needs to fit alongside everything else.",
    },
    {
      n: "II",
      title: "Assessment & environment",
      text: "A thorough movement and lifestyle screen. Together we decide the right training environment — your home, a private gym, or a considered combination.",
    },
    {
      n: "III",
      title: "Your programme",
      text: "A programme written from scratch. Not a template. Structured around your body, your recovery, your calendar and the outcomes that matter to you.",
    },
    {
      n: "IV",
      title: "Delivery & refinement",
      text: "Ongoing one-to-one sessions with honest reviews, clear progress markers and calm adjustments as your body responds.",
    },
  ];

  return (
    <section
      data-testid="london-how"
      className="relative py-24 sm:py-32 bg-[#0a0a0a]"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="max-w-3xl mb-16">
          <p className="overline">How private personal training works</p>
          <h2 className="headline text-[#f4f4f0] text-3xl sm:text-4xl lg:text-5xl mt-5">
            A quiet, deliberate process.
          </h2>
        </div>
        <ol className="grid md:grid-cols-2 lg:grid-cols-4 border-t border-white/5">
          {steps.map((s, i) => (
            <li
              key={s.n}
              data-testid={`london-step-${i}`}
              className={`p-8 lg:p-10 border-b border-white/5 ${
                i < 3 ? "md:border-r" : ""
              } border-white/5 hover:bg-[#111] transition-colors duration-500`}
            >
              <span className="font-serif italic text-[#D4AF37] text-sm tracking-widest">
                {s.n}
              </span>
              <h3 className="font-serif text-2xl text-[#f4f4f0] mt-4">
                {s.title}
              </h3>
              <p className="text-[#a1a1aa] mt-4 leading-relaxed text-[15px]">
                {s.text}
              </p>
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
};

// ------------- AREAS SERVED -------------
const Areas = () => {
  const areas = [
    { name: "Chelsea", note: "SW3" },
    { name: "Kensington", note: "W8" },
    { name: "Knightsbridge & Belgravia", note: "SW1" },
    { name: "Fulham", note: "SW6 / SW10" },
    { name: "Mayfair & Marylebone", note: "W1" },
    { name: "Notting Hill & Holland Park", note: "W11" },
  ];
  return (
    <section
      data-testid="london-areas"
      className="relative py-24 sm:py-32 bg-[#111111] border-y border-white/5"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid lg:grid-cols-12 gap-12 mb-14">
          <div className="lg:col-span-6">
            <p className="overline">Where the coaching happens</p>
            <h2 className="headline text-[#f4f4f0] text-3xl sm:text-4xl lg:text-5xl mt-5">
              Areas served across London.
            </h2>
          </div>
          <div className="lg:col-span-5 lg:col-start-8 flex items-end">
            <p className="text-[#a1a1aa] text-base leading-relaxed">
              Sessions are delivered privately — most often at the client's
              home, and where appropriate at a suitable private gym or
              selected location. Wider London is possible; enquire and we
              will discuss what makes sense.
            </p>
          </div>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-white/5">
          {areas.map((a, i) => (
            <div
              key={a.name}
              data-testid={`london-area-${i}`}
              className="bg-[#0a0a0a] p-8 flex items-center justify-between"
            >
              <span className="font-serif text-xl text-[#f4f4f0]">
                {a.name}
              </span>
              <span className="text-[11px] tracking-[0.25em] uppercase text-[#71717a]">
                {a.note}
              </span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ------------- FOR BUSY PROFESSIONALS -------------
const Professionals = () => (
  <section
    data-testid="london-pros"
    className="relative py-24 sm:py-32 bg-[#0a0a0a]"
  >
    <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-12 items-center">
      <div className="lg:col-span-5">
        <div className="relative aspect-[4/5] overflow-hidden">
          <img
            src={HERO_IMG}
            alt="Private one-to-one personal training with Mateo Bryson"
            className="w-full h-full object-cover object-top grayscale-[15%]"
            loading="lazy"
          />
          <div className="absolute inset-0 ring-1 ring-[#D4AF37]/20 pointer-events-none" />
        </div>
      </div>
      <div className="lg:col-span-7">
        <p className="overline">For a demanding schedule</p>
        <h2 className="headline text-[#f4f4f0] text-3xl sm:text-4xl lg:text-5xl mt-5">
          Built for the way you actually live.
        </h2>
        <div className="divider-gold my-8" />
        <div className="space-y-5 text-[#a1a1aa] text-base sm:text-lg leading-relaxed">
          <p>
            Most of the people Mateo works with have complicated diaries and
            zero appetite for wasted time. Sessions are therefore designed to
            be efficient, honest and genuinely enjoyable — never punishing for
            the sake of it.
          </p>
          <p>
            You will not find over-produced &ldquo;transformations&rdquo; or
            aggressive marketing here. What you will find is a coach who
            listens carefully, plans meticulously, and quietly holds you to
            the standard you set for yourself.
          </p>
        </div>
        <div className="mt-10 flex flex-wrap gap-6 text-[11px] tracking-[0.25em] uppercase text-[#a1a1aa]">
          <span className="flex items-center gap-2">
            <Check className="h-3.5 w-3.5 text-[#D4AF37]" /> 10 Yrs Coaching
          </span>
          <span className="flex items-center gap-2">
            <Check className="h-3.5 w-3.5 text-[#D4AF37]" /> REPs L3 &amp; L4
          </span>
          <span className="flex items-center gap-2">
            <Check className="h-3.5 w-3.5 text-[#D4AF37]" /> Rehab Certified
          </span>
        </div>
      </div>
    </div>
  </section>
);

// ------------- FAQ -------------
const FAQ = () => {
  const faqs = [
    {
      q: "Where do the sessions take place?",
      a: "Most often in the client's own home. Where useful, Mateo can also arrange sessions at a suitable private gym or another appropriate location in London. The right setting is discussed and decided together.",
    },
    {
      q: "Do I need my own equipment?",
      a: "No. Mateo brings the necessary equipment for home-based sessions. If a private gym environment is more appropriate for your goals, that can be arranged instead.",
    },
    {
      q: "Is this suitable if I've never trained one-to-one before?",
      a: "Yes. A significant proportion of Mateo's clients come to private personal training after time away from structured exercise, or having only trained in group settings. The starting point is always where you actually are — never where you feel you 'should' be.",
    },
    {
      q: "How often will I train?",
      a: "Typically two to four times per week, depending on your goals, recovery capacity and diary. This is decided at the initial consultation and revisited as things progress.",
    },
    {
      q: "Can training work around injury or a specific medical history?",
      a: "Yes. Mateo is rehab-certified and comfortable collaborating with physiotherapists, osteopaths and consultants where appropriate — ensuring continuity of care across the people supporting you.",
    },
    {
      q: "How do I begin?",
      a: "With a short, private conversation — no pressure and no sales pitch. Request a consultation via the form on the homepage, or call 07386 710 866.",
    },
  ];
  return (
    <section
      data-testid="london-faq"
      className="relative py-24 sm:py-32 bg-[#111111] border-y border-white/5"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-4">
          <p className="overline">Questions</p>
          <h2 className="headline text-[#f4f4f0] text-3xl sm:text-4xl lg:text-5xl mt-5 sticky top-28">
            Personal Training Questions, <br />
            <span className="italic text-[#D4AF37]">
              Answered.
            </span>
          </h2>
        </div>
        <div className="lg:col-span-8">
          <Accordion
            type="single"
            collapsible
            className="w-full"
            data-testid="london-faq-accordion"
          >
            {faqs.map((f, i) => (
              <AccordionItem
                key={i}
                value={`item-${i}`}
                className="border-b border-white/10"
                data-testid={`london-faq-item-${i}`}
              >
                <AccordionTrigger className="font-serif text-left text-[18px] sm:text-[22px] text-[#f4f4f0] hover:text-[#D4AF37] hover:no-underline py-6">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-[#a1a1aa] text-base leading-relaxed pb-6 max-w-2xl">
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </div>
    </section>
  );
};

// ------------- CTA BAND -------------
const CtaBand = () => (
  <section
    data-testid="london-cta"
    className="relative py-24 sm:py-28 bg-[#0a0a0a]"
  >
    <div className="max-w-7xl mx-auto px-6 lg:px-10">
      <div className="relative overflow-hidden bg-[#111111] border border-[#D4AF37]/30 p-10 sm:p-16 grid lg:grid-cols-12 gap-10 items-center">
        <div className="absolute -top-24 -right-24 w-72 h-72 rounded-full bg-[#D4AF37]/5 blur-3xl pointer-events-none" />
        <div className="lg:col-span-8 relative">
          <p className="overline">Begin the conversation</p>
          <h2 className="headline text-[#f4f4f0] text-3xl sm:text-4xl lg:text-5xl mt-5">
            Request a private{" "}
            <span className="italic text-[#D4AF37]">consultation.</span>
          </h2>
          <p className="mt-6 text-[#a1a1aa] text-base sm:text-lg leading-relaxed max-w-xl">
            A short, unhurried call to discuss what you want to achieve and
            whether working together is the right fit. No obligation.
          </p>
        </div>
        <div className="lg:col-span-4 relative flex flex-col sm:flex-row lg:flex-col lg:items-end gap-3">
          <Link
            to="/#contact"
            data-testid="london-cta-form"
            onClick={() =>
              trackEvent("cta_book_consultation_click", {
                location: "london_cta",
              })
            }
            className="inline-flex items-center justify-center gap-3 bg-[#D4AF37] text-black px-8 py-4 text-[12px] tracking-[0.22em] uppercase font-medium hover:bg-[#f0e6d2] transition-colors"
          >
            Request Consultation
            <ArrowRight className="h-4 w-4" />
          </Link>
          <a
            href={`https://wa.me/${PHONE_INTL}?text=${WHATSAPP_TEXT}`}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="london-cta-whatsapp"
            onClick={() =>
              trackEvent("whatsapp_click", { location: "london_cta" })
            }
            className="inline-flex items-center justify-center gap-3 border border-white/20 text-[#f4f4f0] px-8 py-4 text-[12px] tracking-[0.22em] uppercase hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors"
          >
            <MessageCircle className="h-4 w-4" />
            WhatsApp
          </a>
        </div>
      </div>
    </div>
  </section>
);

// ------------- FOOTER -------------
const Footer = () => (
  <footer
    data-testid="london-footer"
    className="relative bg-[#0a0a0a] border-t border-white/5 py-14"
  >
    <div className="max-w-7xl mx-auto px-6 lg:px-10">
      <div className="grid md:grid-cols-3 gap-10 pb-10 border-b border-white/5">
        <div>
          <Link to="/" className="flex items-center gap-3">
            <span className="h-8 w-8 border border-[#D4AF37]/60 flex items-center justify-center text-[#D4AF37] font-serif text-sm tracking-widest">
              M
            </span>
            <span className="font-serif text-base tracking-widest text-[#f4f4f0]">
              MATEO BRYSON
            </span>
          </Link>
          <p className="mt-5 text-sm text-[#a1a1aa] leading-relaxed max-w-xs">
            Private personal training across London — delivered at home, in
            private gyms and selected locations.
          </p>
        </div>
        <div>
          <div className="text-[10px] tracking-[0.3em] uppercase text-[#71717a]">
            Contact
          </div>
          <ul className="mt-5 space-y-3 text-sm">
            <li>
              <a
                href={`tel:${PHONE}`}
                className="text-[#f4f4f0] hover:text-[#D4AF37] transition-colors"
              >
                07386 710 866
              </a>
            </li>
            <li>
              <a
                href={`mailto:${EMAIL}`}
                className="text-[#f4f4f0] hover:text-[#D4AF37] transition-colors break-all"
              >
                mateobrysonpt@gmail.com
              </a>
            </li>
            <li>
              <a
                href={`https://wa.me/${PHONE_INTL}?text=${WHATSAPP_TEXT}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#f4f4f0] hover:text-[#D4AF37] transition-colors"
              >
                WhatsApp
              </a>
            </li>
          </ul>
        </div>
        <div>
          <div className="text-[10px] tracking-[0.3em] uppercase text-[#71717a]">
            More
          </div>
          <ul className="mt-5 space-y-3 text-sm">
            <li>
              <Link
                to="/"
                className="text-[#f4f4f0] hover:text-[#D4AF37] transition-colors"
              >
                Homepage
              </Link>
            </li>
            <li>
              <a
                href="/#services"
                className="text-[#f4f4f0] hover:text-[#D4AF37] transition-colors"
              >
                Services
              </a>
            </li>
            <li>
              <a
                href="/#pricing"
                className="text-[#f4f4f0] hover:text-[#D4AF37] transition-colors"
              >
                Pricing
              </a>
            </li>
            <li>
              <Link
                to="/privacy"
                className="text-[#f4f4f0] hover:text-[#D4AF37] transition-colors"
              >
                Privacy
              </Link>
            </li>
          </ul>
        </div>
      </div>
      <div className="pt-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 text-[11px] tracking-[0.2em] uppercase text-[#71717a]">
        <div>
          © {new Date().getFullYear()} Mateo Bryson · All rights reserved
        </div>
        <div>Private Personal Trainer · London</div>
      </div>
    </div>
  </footer>
);

// ------------- PAGE -------------
export default function London() {
  useSeoHead();
  return (
    <div className="relative bg-[#0a0a0a] text-[#f4f4f0] font-sans overflow-x-hidden">
      <Nav />
      <Hero />
      <Intro />
      <Pillars />
      <HowItWorks />
      <Professionals />
      <Areas />
      <FAQ />
      <CtaBand />
      <Footer />
    </div>
  );
}
